import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { CustomerCreateModal } from "@/components/CustomerCreateModal";
import { useAuth } from "@/context/AuthContext";
import { Pagination } from "@/components/Pagination";
import { fmtCurrency } from "@/lib/format";
import { MagnifyingGlass, PencilSimple, ChartBar, Plus, UploadSimple, Trash, Warning } from "@phosphor-icons/react";

const TH = "px-3 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";

export default function CustomersPage() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [tick, setTick] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/customers/${delTarget.id}`);
      toast.success(t.customerDeleted);
      setDelTarget(null);
      setTick((v) => v + 1);
    } catch (e) { toast.error(e.response?.data?.detail || t.deleteFailed); } finally { setDeleting(false); }
  };

  useEffect(() => {
    setLoading(true);
    const tm = setTimeout(() => {
      axios.get(`${API}/customers`, { params: q ? { q } : {} })
        .then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(tm);
  }, [q, tick]);
  useEffect(() => { setPage(1); }, [q, perPage]);
  const paged = rows.slice((page - 1) * perPage, page * perPage);

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/customers/import-addresses`, fd);
      toast.success(`${data.customers_updated} ${t.clients} — ${t.saved}`);
      setTick((v) => v + 1);
    } catch (err) { toast.error(err.response?.data?.detail || t.saveFailed); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="px-4 sm:px-6 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.backOffice}</div>
            <h1 className="mt-1 font-display text-4xl font-black tracking-tighter text-foreground">{t.clients}</h1>
          </div>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onImport} data-testid="import-addresses-input" />
            <button data-testid="import-addresses-button" onClick={() => fileRef.current?.click()} disabled={importing}
              className="inline-flex items-center gap-2 border border-border px-4 py-3 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200 disabled:opacity-60">
              <UploadSimple size={16} weight="bold" /> {importing ? t.loading : t.importAddresses}
            </button>
            <button data-testid="new-customer-button" onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
              <Plus size={16} weight="bold" /> {t.newCustomer}
            </button>
          </div>
        </div>

        <div className="mb-5 flex items-center border-2 border-border bg-card px-4 focus-within:border-primary transition-colors duration-200">
          <MagnifyingGlass size={20} weight="bold" className="text-muted-foreground" />
          <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`${t.searchCustomers}`}
            className="w-full bg-transparent px-3 py-3.5 text-sm text-foreground outline-none placeholder-muted-foreground" />
          <span className="hidden text-xs text-muted-foreground sm:inline">{rows.length}</span>
        </div>

        <div className="mb-4 flex justify-end">
          <Pagination testid="customers-pagination-top" page={page} perPage={perPage} total={rows.length} onPage={setPage} onPerPage={setPerPage} />
        </div>

        <div className="border border-border bg-card overflow-x-auto">
          <table data-testid="customers-table" className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b-2 border-border text-left">
                <th className={TH}>{t.businessName}</th>
                <th className={TH}>{t.coopClientCode}</th>
                <th className={TH}>{t.email}</th>
                <th className={TH}>{t.phone}</th>
                <th className={`${TH} text-right`}>{t.salesYtd}</th>
                <th className={`${TH} text-right`}>{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t.loading}</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t.noResults}</td></tr>}
              {!loading && paged.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => navigate(`/admin/customers/${c.id}`)} className="cursor-pointer border-b border-border hover:bg-muted/40 transition-colors duration-150">
                  <td className="px-3 py-2 font-medium text-foreground"><span data-testid={`customer-name-link-${c.id}`} className="hover:text-primary hover:underline">{c.business_name}</span></td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{c.coop_client_code || "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{c.email || "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{c.phone || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground">{fmtCurrency(c.ytd_current, lang)}</td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`report-customer-${c.id}`} onClick={() => navigate(`/admin/customers/${c.id}`)}
                        className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200">
                        <ChartBar size={14} weight="bold" /> {t.viewReport}
                      </button>
                      <button data-testid={`edit-customer-${c.id}`} onClick={() => navigate(`/admin/customers/${c.id}/edit`)}
                        className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200">
                        <PencilSimple size={14} weight="bold" /> {t.edit}
                      </button>
                      {isAdmin && (
                        <button data-testid={`delete-customer-${c.id}`} onClick={() => setDelTarget(c)} title={t.deletePermanently}
                          className="inline-flex items-center justify-center border border-red-300 px-2.5 py-1.5 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors duration-200">
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex justify-end">
          <Pagination testid="customers-pagination-bottom" page={page} perPage={perPage} total={rows.length} onPage={setPage} onPerPage={setPerPage} />
        </div>
        <CustomerCreateModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={(c) => navigate(`/admin/customers/${c.id}/edit`)} />

        {delTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-customer-modal">
            <div className="w-full max-w-md border border-border bg-card p-6 shadow-xl">
              <div className="mb-4 flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"><Warning size={22} weight="bold" /></div>
                <div>
                  <h2 className="font-display text-lg font-black tracking-tight text-foreground">{t.deleteCustomerTitle}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{t.deleteCustomerWarning}</p>
                </div>
              </div>
              <div className="mb-5 border border-border bg-muted/40 px-3 py-2.5">
                <div className="text-sm font-semibold text-foreground">{delTarget.business_name}</div>
                <div className="font-mono text-[11px] text-muted-foreground">{delTarget.coop_client_code || delTarget.email || ""}</div>
              </div>
              <div className="flex justify-end gap-3">
                <button data-testid="delete-customer-cancel" onClick={() => setDelTarget(null)} disabled={deleting}
                  className="border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted disabled:opacity-60">{t.cancel}</button>
                <button data-testid="delete-customer-confirm" onClick={confirmDelete} disabled={deleting}
                  className="inline-flex items-center gap-2 bg-red-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-red-700 disabled:opacity-60">
                  <Trash size={14} weight="bold" /> {t.deletePermanently}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
